<script setup lang="ts">
import { Ref, ref, toRefs, computed } from "vue";
import { useApiClient } from "@/api/client";
import DimensionValueDO = App.Data.DimensionValueDO;
import { debounce } from "vue-debounce";
import { useI18n } from "vue-i18n";
import DimensionDO = App.Data.DimensionDO;
import ReverseDimensionSearchDTO = App.Data.ReverseDimensionSearchDTO;
const { t } = useI18n();

const props = defineProps<{
	resultsLimit: number;
}>();

const emit = defineEmits(["dimension-selected"]);

const isDialogVisible = defineModel<boolean>();

const isLoading: Ref<boolean> = ref(false);
const filteredValues: Ref<ReverseDimensionSearchDTO[]> = ref();

const search = debounce(async (searchValue: string) => {
	isLoading.value = true;

	const reverseDimensionSearchResponse = await useApiClient(
		"explorer"
	).reverseDimensionSearch(searchValue, props.resultsLimit);

	if (reverseDimensionSearchResponse.isOk()) {
		filteredValues.value = reverseDimensionSearchResponse.value;
	}

	isLoading.value = false;
}, "300ms");

const emitSelectedDimension = (
	selectedReverseDimensionSearchDTO: ReverseDimensionSearchDTO
) => {
	emit(
		"dimension-selected",
		selectedReverseDimensionSearchDTO.dimension,
		selectedReverseDimensionSearchDTO.specification ?? null
	);
	isDialogVisible.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" width="700">
		<v-card>
			<v-autocomplete
				:autofocus="true"
				:loading="isLoading"
				:items="filteredValues"
				item-title="display_name"
				item-value="display_name"
				return-object
				hide-details
				placeholder="Search for possible values over all dimensions"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				@update:search="(searchValue) => search(searchValue)"
				@update:modelValue="emitSelectedDimension"
			>
				<template #item="{ item, props }">
					<v-list-item v-bind="props" :value="item.display_name">
						<template #title>
							{{ item.raw.display_name }}
						</template>

						<template #append>
							<strong>
								{{ t(`properties.${item.raw.dimension.name_clean}.title`) }}
							</strong>
						</template>
					</v-list-item>
				</template>

				<template #append-item>
					<v-list-item
						v-if="filteredValues?.length === props.resultsLimit"
						density="compact"
						class="text-medium-emphasis"
					>
						<template #title>
							Please refine your search to see more results ..
						</template>
					</v-list-item>
				</template>
			</v-autocomplete>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
